import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Send, Shield, Clock, Globe, ArrowRight, ShieldCheck } from 'lucide-react';
import { useApp } from '../context/AppContext';
import Layout from '../components/Layout';

const Home = () => {
  const { user } = useApp();
  const navigate = useNavigate();

  const features = [
    {
      icon: Send,
      title: 'Envíos Rápidos',
      description: 'Transfiere dinero en minutos a cualquier país',
      path: '/send/step1',
      highlight: true // Resalta esta tarjeta principal
    },
    {
      icon: Shield,
      title: 'Seguro y Confiable',
      description: 'Tus transacciones están 100% protegidas'
    },
    {
      icon: Clock,
      title: '24/7 Disponible',
      description: 'Envía dinero cuando lo necesites, sin esperas'
    },
    {
      icon: Globe,
      title: 'Cobertura Global',
      description: 'Conectamos países de toda América Latina'
    }
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-[#f4f7fb] px-5 py-8 md:py-12 pb-24 overflow-hidden">
        <div className="mx-auto w-full max-w-lg">
          
         {/* =========================================
    HEADER Y LOGO (PROPORCIONAL 5XL)
    ========================================= */}
<motion.div 
  className="text-center mb-12 flex flex-col items-center"
  initial={{ opacity: 0, y: -20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.5 }}
>
  {/* CONTENEDOR DEL LOGO UNIFICADO */}
  <div className="flex items-center gap-5 mb-10 md:mt-6 transition-transform duration-300">
    
    {/* 🔥 ICONO PROPORCIONAL A 5XL (100px) */}
    <img
      src="/logo-icono.png" 
      alt="Logo Icono"
      className="w-[80px] h-[80px] md:w-[100px] md:h-[100px] object-contain rounded-[24px] shadow-lg shadow-blue-900/10"
    />
    
    {/* TEXTO EN 5XL */}
    <div className="flex flex-col text-left">
      <span className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.85] text-blue-950">
        Remesas
      </span>
      <span className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.85] text-blue-600">
        Venezuela
      </span>
    </div>
  </div>
            
            <h1 className="text-3xl font-black text-slate-800 tracking-tight mb-2 leading-tight">
              {user ? `¡Hola, ${user.first_name || 'Usuario'}! ` : 'Cargando...'}
            </h1>
            <p className="text-slate-500 font-medium text-sm">
              ¿A dónde enviaremos dinero hoy?
            </p>
          </motion.div>

          {/* =========================================
              BANNER PRINCIPAL INTERACTIVO
              ========================================= */}
          <motion.div 
            className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-900 rounded-[32px] p-8 mb-10 text-white cursor-pointer shadow-[0_15px_30px_rgba(37,99,235,0.3)] hover:shadow-[0_20px_40px_rgba(37,99,235,0.4)] transition-all duration-300 active:scale-[0.98] group"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            onClick={() => navigate('/send/step1')}
          >
            {/* Decoración de fondo */}
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all duration-500"></div>
            <div className="absolute -bottom-6 -right-4 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
              <Globe className="w-32 h-32" />
            </div>

            <div className="relative z-10">
              <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest mb-4 border border-white/10 shadow-sm">
                Mejor Tasa Garantizada
              </span>
              <h2 className="text-3xl font-black mb-2 tracking-tight">Enviar dinero</h2>
              <p className="text-blue-100 font-medium mb-8 max-w-[85%] leading-relaxed text-sm">
                Rápido, seguro y sin complicaciones. Comienza tu transferencia ahora.
              </p>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white text-blue-600 rounded-2xl flex items-center justify-center group-hover:translate-x-2 transition-transform duration-300 shadow-lg">
                  <ArrowRight className="w-6 h-6" />
                </div>
                <span className="font-black uppercase tracking-widest text-xs">Comenzar</span>
              </div>
            </div>
          </motion.div>

          {/* =========================================
              GRID DE CARACTERÍSTICAS (FEATURES)
              ========================================= */}
          <div className="grid grid-cols-2 gap-4 mb-10">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isClickable = !!feature.path;
              const isHighlight = feature.highlight;

              return (
                <motion.div
                  key={index}
                  className={`relative overflow-hidden rounded-[24px] p-5 shadow-sm border transition-all duration-300 flex flex-col justify-between
                    ${isClickable ? 'cursor-pointer active:scale-95 hover:shadow-md' : ''}
                    ${isHighlight 
                      ? 'bg-blue-50 border-blue-100 hover:bg-blue-100/50' 
                      : 'bg-white border-slate-100 hover:border-blue-100 hover:bg-slate-50'
                    }
                  `}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.4 }}
                  onClick={() => isClickable && navigate(feature.path)}
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 shrink-0 transition-colors
                    ${isHighlight ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-slate-50 text-blue-600 border border-slate-100'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={`font-black mb-1.5 leading-tight tracking-tight text-[14px]
                      ${isHighlight ? 'text-blue-950' : 'text-slate-800'}`}>
                      {feature.title}
                    </h3>
                    <p className={`text-[11px] font-bold leading-relaxed
                      ${isHighlight ? 'text-blue-700/80' : 'text-slate-400'}`}>
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* =========================================
              SECCIÓN SOBRE NOSOTROS / CONFIANZA
              ========================================= */}
          <motion.div 
            className="bg-white rounded-[32px] p-8 shadow-sm border border-slate-100 relative overflow-hidden flex flex-col md:flex-row gap-6 md:items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            {/* Adorno visual sutil */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50/50 rounded-bl-[100px] pointer-events-none"></div>
            
            <div className="relative z-10 flex-1">
              <h3 className="font-black text-slate-800 mb-2 tracking-tight text-xl">Nuestra Promesa</h3>
              <p className="text-slate-500 leading-relaxed text-sm mb-6 font-medium">
                Remesas a Venezuela rápidas, seguras y transparentes. La manera más confiable de enviar apoyo desde el exterior a tus seres queridos.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-50 text-green-700 rounded-xl border border-green-100 shadow-sm">
                <ShieldCheck className="w-5 h-5 shrink-0" />
                <span className="text-[10px] font-black uppercase tracking-widest mt-0.5">Regulados y Seguros</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
};

export default Home;

